package modysy.simulation.pp

import modysy.model.Agent
import modysy.tools.EnergyStorage

import scala.collection.mutable
import scala.util.Random

/** A class for simulating a population protocol.
  * Contains transition rules and involved states.
  * Add rules, states and simulate interactions.
  */
class PopulationProtocol() {

  private val states: mutable.HashMap[String, State] = new mutable.HashMap()
  private val rules: mutable.HashMap[(String, String), List[Rule]] =
    new mutable.HashMap()

  /** Adds an state with a unique name to the population protocol.
    * No change is done if the name is already contained inside
    * the population protocol and false is returned.
    *
    * @param state the state to be added
    * @return true on success, false if name is not unique
    */
  def addState(state: State): Boolean = {
    //state mustn't exist
    if (states contains state.name)
      return false

    states.put(state.name, state)
    true
  }

  /** Adds a rule to the population protocol taking the order of initiator and respondent into account.
    * If any state inside the rule was not already added to the population protocol
    * than it is added requiring an unique state name as in addState(...).
    *
    * Returns false if any new state does not have an unique name, true otherwise.
    * If false is returned the rule is not added, but some new states may be.
    *
    * @see addState
    * @see addRuleSymmetric
    * @param rule_new the rule to be added
    * @return true on success, false if any new state has no unique name
    */
  def addRule(rule_new: Rule): Boolean = {

    //Add state in case it's not part of the PP yet
    if (!(states.values.toSet contains rule_new.startInitiator)) {
      if (!addState(rule_new.startInitiator)) {
        return false
      }
    }

    if (!(states.values.toSet contains rule_new.startRespondent)) {
      if (!addState(rule_new.startRespondent)) {
        return false
      }
    }

    if (!(states.values.toSet contains rule_new.resultInitiator)) {
      if (!addState(rule_new.resultInitiator)) {
        return false
      }
    }

    if (!(states.values.toSet contains rule_new.resultRespondent)) {
      if (!addState(rule_new.resultRespondent)) {
        return false
      }
    }

    val states1 = (rule_new.startInitiator.name, rule_new.startRespondent.name)

    val list_prev = this.rules.remove(states1)
    list_prev match {
      case None    => rules.put(states1, List(rule_new))
      case Some(l) => rules.put(states1, rule_new :: l)
    }

    true
  }

  /** Adds a rule to the population protocol ignoring the order of initiator and respondent.
    * If any state inside the rule was not already added to the population protocol
    * than it is added requiring an unique state name as in addState(...).
    *
    * Returns false if any new state does not have an unique name, true otherwise.
    * If false is returned the rule is not added, but some new states may be.
    *
    * @see addState
    * @see addRule
    * @param rule_new the rule to be added
    * @return true on success, false if any new state has no unique name
    */
  def addRuleSymmetric(rule_new: Rule): Boolean = {

    if (!addRule(rule_new))
      return false

    addRule(
      Rule(
        rule_new.startRespondent,
        rule_new.startInitiator,
        rule_new.resultRespondent,
        rule_new.resultInitiator,
        rule_new.energyGainOnInteract
      )
    )
    true
  }

  /** Returns the state with the given name or Nothing if no state is found with that name
    *
    * @param name the state name to look for
    * @return the state with the given name inside a Some(...) or Nothing
    */
  def getStateByName(name: String): Option[State] = {
    states.get(name)
  }

  /** Simulates an interaction between the given two agents and changes their states accordingly.
    * One fitting rules of the rules added to the population protocol is selected and
    * the states of the given agents are changed according to that selected rule.
    *
    * The order of the initiator and the respondent matters.
    *
    * Returns false and no operation if performed if no rule fits, otherwise returns true.
    *
    * @param init          the initiator of this interaction
    * @param resp          the respondent to this interaction
    * @param energyStorage the energyStorage to add any gain from the interaction
    * @return true on success, false otherwise
    */
  def interact(
      init: Agent,
      resp: Agent,
      energyStorage: EnergyStorage
  ): Boolean = {

    val fittingRules = rules.get((init.state.name, resp.state.name))

    fittingRules match {

      //no rule found
      case None =>
        return false
      //only one rule (deterministic)
      case Some(List(rule1)) =>
        init.state = rule1.resultInitiator
        resp.state = rule1.resultRespondent
        energyStorage.addEnergy(rule1.energyGainOnInteract)

      //multiple rules (non-deterministic)
      case Some(l) =>
        val appliedRule = {
          val r = new Random();
          l(r.nextInt(l.size))
        }
        init.state = appliedRule.resultInitiator
        resp.state = appliedRule.resultRespondent
        energyStorage.addEnergy(appliedRule.energyGainOnInteract)
    }
    true
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/simulation/pp/PopulationProtocol.scala